import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Chess } from 'chess.js';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../api';
import { useDataCache } from '../DataCacheContext';
import InteractiveBoard, { playSound, getSoundType } from './InteractiveBoard';
import useStockfish from '../hooks/useStockfish';

export default function OpeningTrainerPanel({ c, pieceSet, boardTheme, setPieceSet, setBoardTheme }) {
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username') || '';
  const cache = useDataCache();

  // ── Data fetching with cache ──
  const [loading, setLoading] = useState(true);
  const [mistakes, setMistakes] = useState([]);
  const [error, setError] = useState(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);

  useEffect(() => {
    if (!username) { setLoading(false); setError('No username found'); return; }

    // Check cache first
    const cachedMistakes = cache.get(`opening-mistakes:${username}`);
    if (cachedMistakes) {
      setMistakes(cachedMistakes);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Use the new /opening-mistakes endpoint (server does analysis + Supabase storage)
    fetch(`${API_BASE}/opening-mistakes?username=${encodeURIComponent(username)}`)
      .then(r => { if (!r.ok) throw new Error(`Failed to load opening analysis (${r.status})`); return r.json(); })
      .then(data => {
        // Normalize field names (backend may return camelCase or snake_case)
        const normalized = data.map(m => ({
          openingName: m.openingName || m.opening_name,
          fen: m.fen,
          playerColor: m.playerColor || m.player_color,
          playerMove: m.playerMove || m.player_move,
          bookMove: m.bookMove || m.book_move,
          count: m.count || m.mistake_count || 1,
          totalGames: m.totalGames || m.total_games,
          winRate: m.winRate || m.win_rate,
          precedingMoves: m.precedingMoves || m.preceding_moves || [],
          opponents: m.opponents || m.sample_opponents || [],
          dates: m.dates || m.sample_dates || [],
        }));
        cache.set(`opening-mistakes:${username}`, normalized);
        setMistakes(normalized);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [username, fetchAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation: openings list → positions → drill ──
  const [selectedOpening, setSelectedOpening] = useState(null);
  const [selectedMistake, setSelectedMistake] = useState(null);
  const [viewColor, setViewColor] = useState('white');

  // Group mistakes by opening name + color, split into white/black lists sorted by most played
  const { whiteSummaries, blackSummaries } = useMemo(() => {
    const map = {};
    for (const m of mistakes) {
      const key = `${m.openingName}::${m.playerColor}`;
      if (!map[key]) map[key] = { openingName: m.openingName, playerColor: m.playerColor, totalGames: 0, positions: 0, totalMistakes: 0, _winRateSum: 0 };
      const s = map[key];
      s.positions += 1;
      s.totalMistakes += m.count;
      if (m.totalGames > s.totalGames) s.totalGames = m.totalGames;
      s._winRateSum += m.winRate * m.count;
    }
    const all = Object.values(map).map(s => ({
      ...s,
      avgWinRate: s.totalMistakes > 0 ? Math.round(s._winRateSum / s.totalMistakes) : 0,
    }));
    const byGames = (a, b) => b.totalGames - a.totalGames;
    return {
      whiteSummaries: all.filter(s => s.playerColor === 'white').sort(byGames),
      blackSummaries: all.filter(s => s.playerColor === 'black').sort(byGames),
    };
  }, [mistakes]);

  // Auto-select first white opening + first position on load
  useEffect(() => {
    if (whiteSummaries.length > 0 && !selectedOpening && !selectedMistake) {
      const first = whiteSummaries[0];
      const key = `${first.openingName}::white`;
      const firstPos = mistakes.find(m => m.openingName === first.openingName && m.playerColor === 'white');
      if (firstPos) {
        setSelectedOpening(key);
        setSelectedMistake(firstPos);
      }
    }
  }, [whiteSummaries]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Board state ──
  const [chess] = useState(() => new Chess());
  const [history, setHistory] = useState([]);
  const [moveIndex, setMoveIndex] = useState(0);
  const [positionsArr, setPositionsArr] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragPiece, setDragPiece] = useState(null);
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const [arrows, setArrows] = useState([]);
  const [highlightedSquares, setHighlightedSquares] = useState(new Set());
  const [pendingPromotion, setPendingPromotion] = useState(null);
  // ── Puzzle feedback ──
  const [puzzleFeedback, setPuzzleFeedback] = useState(null);
  const [pStats, setPStats] = useState({ correct: 0, total: 0 });
  const [puzzleStartIdx, setPuzzleStartIdx] = useState(0);

  // ── Stockfish engine (optional) ──
  const [sfEnabled, setSfEnabled] = useState(false);
  const { evaluatePosition: sfEvaluate, ready: sfReady } = useStockfish(16);
  const [sfEval, setSfEval] = useState(null); // { cpBefore, cpAfter, classification }

  // Load puzzle position — replay preceding moves so user can navigate back
  useEffect(() => {
    if (!selectedMistake) return;
    const preceding = selectedMistake.precedingMoves || [];
    const tempChess = new Chess();
    const positions = [tempChess.fen()];
    const hist = [];
    for (const san of preceding) {
      try {
        const result = tempChess.move(san);
        if (result) { hist.push(result); positions.push(tempChess.fen()); }
        else break;
      } catch { break; }
    }
    chess.load(selectedMistake.fen);
    const startIdx = positions.length - 1;
    setHistory(hist); setPositionsArr(positions); setMoveIndex(startIdx);
    setPuzzleStartIdx(startIdx);
    setSelectedSquare(null); setLegalMoves([]);
    if (hist.length > 0) setLastMove({ from: hist[hist.length - 1].from, to: hist[hist.length - 1].to }); else setLastMove(null);
    setArrows([]); setHighlightedSquares(new Set()); setPendingPromotion(null);
    setPuzzleFeedback(null);
    setSfEval(null);
    setFlipped(selectedMistake.playerColor === 'black');
  }, [selectedMistake]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFen = positionsArr[moveIndex] || chess.fen();
  const boardChess = useMemo(() => { const bc = new Chess(); bc.load(currentFen); return bc; }, [currentFen]);
  const squareName = useCallback((ri, ci) => { const f = flipped ? 7 - ci : ci; const r = flipped ? ri : 7 - ri; return 'abcdefgh'[f] + (r + 1); }, [flipped]);

  // ── Drag-and-drop ──
  const getSquareFromPoint = useCallback((clientX, clientY) => {
    // InteractiveBoard owns the board ref; use DOM query as fallback
    const boardEl = document.querySelector('[data-board-grid]');
    if (!boardEl) return null;
    const rect = boardEl.getBoundingClientRect();
    const x = clientX - rect.left, y = clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
    const col = Math.floor((x / rect.width) * 8), row = Math.floor((y / rect.height) * 8);
    return 'abcdefgh'[flipped ? 7 - col : col] + ((flipped ? row : 7 - row) + 1);
  }, [flipped]);

  const handleDragStart = useCallback((ri, ci, e) => {
    if (puzzleFeedback || moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci); const piece = boardChess.get(sq);
    if (!piece || piece.color !== boardChess.turn()) return;
    e.preventDefault();
    const cX = e.touches ? e.touches[0].clientX : e.clientX; const cY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragFrom(sq); setDragPiece(piece); setDragPos({ x: cX, y: cY });
    setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to));
  }, [moveIndex, positionsArr.length, squareName, boardChess, puzzleFeedback]);

  const isPromotion = useCallback((from, to) => { const p = boardChess.get(from); if (!p || p.type !== 'p') return false; return (p.color === 'w' && to[1] === '8') || (p.color === 'b' && to[1] === '1'); }, [boardChess]);

  const executeMove = useCallback((from, to, promotion = 'q') => {
    try {
      const tempChess = new Chess(currentFen);
      const result = tempChess.move({ from, to, promotion });
      if (result) {
        const nh = [...history.slice(0, moveIndex), result];
        const np = [...positionsArr.slice(0, moveIndex + 1), tempChess.fen()];
        chess.load(tempChess.fen()); setHistory(nh); setPositionsArr(np); setMoveIndex(np.length - 1);
        setLastMove({ from: result.from, to: result.to }); setSelectedSquare(null); setLegalMoves([]);
        playSound(getSoundType(result));
        // Opening puzzle check on first move
        if (moveIndex === puzzleStartIdx && !puzzleFeedback && selectedMistake) {
          const isCorrect = result.san === selectedMistake.bookMove;
          const isRepeat = result.san === selectedMistake.playerMove;
          setPuzzleFeedback({ correct: isCorrect, repeat: isRepeat, movePlayed: result.san, bookMove: selectedMistake.bookMove, playerMove: selectedMistake.playerMove });
          setPStats(s => ({ correct: s.correct + (isCorrect ? 1 : 0), total: s.total + 1 }));
          // Stockfish evaluation of the played move
          if (sfEnabled && sfReady) {
            setSfEval(null);
            const fenBefore = currentFen;
            const fenAfter = tempChess.fen();
            (async () => {
              const before = await sfEvaluate(fenBefore);
              const after = await sfEvaluate(fenAfter);
              if (before && after) {
                const side = result.color === 'w' ? 1 : -1;
                const cpBefore = (before.cp || 0) * side;
                const cpAfter = (after.cp || 0) * -side; // after move, it's opponent's turn
                const drop = cpAfter - cpBefore;
                const cls = drop >= 0 ? 'great' : drop >= -10 ? 'good' : drop >= -50 ? 'inaccuracy' : drop >= -150 ? 'mistake' : 'blunder';
                setSfEval({ cpBefore, cpAfter, drop, classification: cls });
              }
            })();
          }
        }
        return true;
      }
    } catch {}
    return false;
  }, [currentFen, chess, history, positionsArr, moveIndex, puzzleFeedback, selectedMistake, puzzleStartIdx, sfEnabled, sfReady, sfEvaluate]);

  const handlePromotionChoice = useCallback((pt) => { if (!pendingPromotion) return; executeMove(pendingPromotion.from, pendingPromotion.to, pt); setPendingPromotion(null); }, [pendingPromotion, executeMove]);

  useEffect(() => {
    if (!dragFrom) return;
    const onMove = (e) => { setDragPos({ x: e.touches ? e.touches[0].clientX : e.clientX, y: e.touches ? e.touches[0].clientY : e.clientY }); };
    const onEnd = (e) => {
      const cX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX; const cY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
      const dropSq = getSquareFromPoint(cX, cY);
      if (dropSq && dropSq !== dragFrom) {
        if (isPromotion(dragFrom, dropSq)) { setPendingPromotion({ from: dragFrom, to: dropSq, color: boardChess.get(dragFrom).color }); setDragFrom(null); setDragPiece(null); setDragPos(null); return; }
        executeMove(dragFrom, dropSq);
      }
      setDragFrom(null); setDragPiece(null); setDragPos(null);
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onEnd);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onEnd); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
  }, [dragFrom, getSquareFromPoint, isPromotion, executeMove, boardChess]);

  const handleSquareClick = useCallback((ri, ci) => {
    if (puzzleFeedback || moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci); const piece = boardChess.get(sq);
    if (selectedSquare) {
      if (boardChess.moves({ square: selectedSquare, verbose: true }).some(m => m.to === sq)) {
        if (isPromotion(selectedSquare, sq)) { setPendingPromotion({ from: selectedSquare, to: sq, color: boardChess.get(selectedSquare).color }); return; }
        executeMove(selectedSquare, sq); return;
      }
      if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); return; }
      setSelectedSquare(null); setLegalMoves([]); return;
    }
    if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); }
  }, [selectedSquare, boardChess, moveIndex, positionsArr.length, squareName, isPromotion, executeMove, puzzleFeedback]);

  const goTo = useCallback((idx) => {
    const cl = Math.max(0, Math.min(idx, positionsArr.length - 1));
    setMoveIndex(cl); setSelectedSquare(null); setLegalMoves([]);
    if (cl > 0 && history[cl - 1]) setLastMove({ from: history[cl - 1].from, to: history[cl - 1].to }); else setLastMove(null);
  }, [positionsArr, history]);

  const handleUndo = useCallback(() => {
    if (moveIndex <= 0) return;
    const nh = history.slice(0, moveIndex - 1), np = positionsArr.slice(0, moveIndex);
    chess.load(np[np.length - 1]); setHistory(nh); setPositionsArr(np); setMoveIndex(np.length - 1);
    setSelectedSquare(null); setLegalMoves([]);
    if (nh.length > 0) setLastMove({ from: nh[nh.length - 1].from, to: nh[nh.length - 1].to }); else setLastMove(null);
    if (moveIndex === puzzleStartIdx + 1) setPuzzleFeedback(null);
  }, [chess, history, positionsArr, moveIndex, puzzleStartIdx]);

  // Derived
  const turnColor = boardChess.turn();

  const movePairs = []; for (let i = 0; i < history.length; i += 2) movePairs.push({ num: Math.floor(i/2)+1, white: history[i], black: history[i+1]||null, whiteIdx: i+1, blackIdx: i+2 });

  const cardSx = { px:2.5, py:2, borderRadius:'16px', bgcolor:`${c.textMuted}06`, border:`1px solid ${c.textMuted}10` };
  const labelSx = { color:c.textMuted, fontSize:'0.8rem', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', mb:1.5 };
  const accuracy = pStats.total > 0 ? Math.round((pStats.correct / pStats.total) * 100) : 0;

  // ═══ LOADING STATE ═══
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', mt: 2 }}>Analyzing your opening mistakes...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: c.accent, fontSize: '1rem', fontWeight: 700, mb: 1 }}>Could not load games</Typography>
        <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', mb: 2 }}>{error}</Typography>
        <Box onClick={() => setFetchAttempt(a => a + 1)} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 1,
          borderRadius: '10px', cursor: 'pointer', bgcolor: `${c.accent}12`, border: `1.5px solid ${c.accent}30`,
          transition: 'all 0.15s ease', '&:hover': { bgcolor: `${c.accent}20` },
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2" strokeLinecap="round"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>
          <Typography sx={{ color: c.accent, fontSize: '0.85rem', fontWeight: 700 }}>Retry</Typography>
        </Box>
      </Box>
    );
  }

  // ═══ UNIFIED LAYOUT: list on left, board + drill on right ═══
  const openingsList = (
    <Box sx={{ minWidth: 0, overflow: 'auto', maxHeight: { lg: 'calc(100vh - 160px)' }, pr: { lg: 2 } }}>
      {mistakes.length === 0 ? (
        <Box sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 800, mb: 1 }}>No opening mistakes found</Typography>
          <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', lineHeight: 1.7 }}>Play more games and come back — we'll find the positions where you can improve.</Typography>
        </Box>
      ) : (
        <>
          <Typography sx={{ color: c.textDark, fontSize: '1.3rem', fontWeight: 800, mb: 0.5 }}>Opening Mistakes</Typography>
          <Typography sx={{ color: c.textMuted, fontSize: '1rem', mb: 3, lineHeight: 1.6 }}>
            Select a position to drill on the board.
          </Typography>
          {(() => {
            const items = viewColor === 'white' ? whiteSummaries : blackSummaries;
            return (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  {/* Toggle: two circles in a pill */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, borderRadius: '50px', bgcolor: `${c.textMuted}08` }}>
                    <Box onClick={() => { if (viewColor !== 'white') { setViewColor('white'); setSelectedOpening(null); } }} sx={{
                      width: 40, height: 40, borderRadius: '50%', bgcolor: '#ECE2DB', cursor: 'pointer', transition: 'all 0.15s ease',
                      opacity: viewColor === 'white' ? 1 : 0.3,
                      transform: viewColor === 'white' ? 'scale(1)' : 'scale(0.85)',
                      '&:hover': { opacity: viewColor === 'white' ? 1 : 0.6 },
                    }} />
                    <Box onClick={() => { setViewColor(viewColor === 'white' ? 'black' : 'white'); setSelectedOpening(null); }} sx={{ cursor: 'pointer', display: 'flex', px: 0.3, transition: 'all 0.15s ease', '&:hover': { transform: 'scale(1.2)' } }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="2" strokeLinecap="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 014-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 01-4 4H3" /></svg>
                    </Box>
                    <Box onClick={() => { if (viewColor !== 'black') { setViewColor('black'); setSelectedOpening(null); } }} sx={{
                      width: 40, height: 40, borderRadius: '50%', bgcolor: '#111115', cursor: 'pointer', transition: 'all 0.15s ease',
                      opacity: viewColor === 'black' ? 1 : 0.3,
                      transform: viewColor === 'black' ? 'scale(1)' : 'scale(0.85)',
                      '&:hover': { opacity: viewColor === 'black' ? 1 : 0.6 },
                    }} />
                  </Box>
                  <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 700 }}>As {viewColor}</Typography>
                </Box>
                {items.length === 0 ? (
                  <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontStyle: 'italic' }}>No mistakes found as {viewColor}.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    {items.map((s, i) => {
                      const openingKey = `${s.openingName}::${s.playerColor}`;
                      const isExpanded = selectedOpening === openingKey;
                      const positions = isExpanded ? mistakes.filter(m => m.openingName === s.openingName && m.playerColor === s.playerColor) : [];
                      return (
                        <Box key={i}>
                          <Box onClick={() => setSelectedOpening(isExpanded ? null : openingKey)} sx={{
                            px: 2, py: 1.5, borderRadius: '12px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}10`,
                            cursor: 'pointer', transition: 'all 0.15s ease',
                            ...(isExpanded ? { bgcolor: `${c.accent}06`, borderColor: `${c.accent}20` } : {}),
                            '&:hover': { bgcolor: `${c.accent}08`, borderColor: `${c.accent}25` },
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="2.5" strokeLinecap="round" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease', flexShrink: 0 }}><polyline points="9 6 15 12 9 18" /></svg>
                              <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700, flex: 1, minWidth: 0 }}>{s.openingName}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8, ml: 3.2 }}>
                              <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontWeight: 600 }}>{s.totalGames} games played</Typography>
                              <Typography sx={{ color: c.textMuted, fontSize: '0.88rem' }}>·</Typography>
                              <Typography sx={{ color: c.accent, fontSize: '0.88rem', fontWeight: 700 }}>{s.totalMistakes} mistake{s.totalMistakes !== 1 ? 's' : ''}</Typography>
                            </Box>
                          </Box>
                          {isExpanded && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mt: 0.8, ml: 3.2 }}>
                              {positions.map((m, j) => {
                                const isActive = selectedMistake === m;
                                return (
                                  <Box key={j} onClick={() => { setSelectedMistake(m); setPuzzleFeedback(null); }} sx={{
                                    px: 1.5, py: 1, borderRadius: '10px',
                                    bgcolor: isActive ? `${c.accent}12` : `${c.textMuted}04`,
                                    border: `1px solid ${isActive ? c.accent + '30' : c.textMuted + '08'}`,
                                    cursor: 'pointer', transition: 'all 0.15s ease',
                                    '&:hover': { bgcolor: `${c.accent}08`, borderColor: `${c.accent}25` },
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography sx={{ color: isActive ? c.accent : c.textDark, fontSize: '0.95rem', fontWeight: 600 }}>#{j + 1}</Typography>
                                      <Typography sx={{ color: c.textDark, fontSize: '0.9rem' }}>
                                        {m.playerMove} → {m.bookMove}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })()}
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { lg: 'flex-start' } }}>

      {/* Left: openings list */}
      <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 280px' }, maxWidth: { xs: '100%', lg: 280 }, minWidth: 0 }}>
        {openingsList}
      </Box>

      {/* Center: board */}
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <InteractiveBoard
          c={c}
          fen={currentFen}
          flipped={flipped}
          pieceSet={pieceSet}
          boardTheme={boardTheme}
          selectedSquare={selectedMistake ? selectedSquare : null}
          legalMoves={selectedMistake ? legalMoves : []}
          lastMove={selectedMistake ? lastMove : null}
          onSquareClick={selectedMistake ? (ri, ci) => handleSquareClick(ri, ci) : () => {}}
          onDragStart={selectedMistake ? handleDragStart : () => {}}
          onFlip={() => setFlipped(f => !f)}
          onGoTo={selectedMistake ? goTo : () => {}}
          setArrows={setArrows}
          setHighlightedSquares={setHighlightedSquares}
          setPieceSet={setPieceSet}
          setBoardTheme={setBoardTheme}
          arrows={arrows}
          highlightedSquares={highlightedSquares}
          dragFrom={dragFrom}
          dragPos={dragPos}
          dragPiece={dragPiece}
          hoveredSquare={hoveredSquare}
          setHoveredSquare={setHoveredSquare}
          pendingPromotion={pendingPromotion}
          onPromotionChoice={handlePromotionChoice}
          setPendingPromotion={setPendingPromotion}
          positions={positionsArr}
          moveIndex={moveIndex}
        />
      </Box>

      {/* Right: drill panel (same structure as free study) */}
      <Box sx={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>

        {!selectedMistake ? (
          <Box sx={{ ...cardSx, textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>Select a position to start drilling</Typography>
          </Box>
        ) : (
          <>
            {/* Turn indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...cardSx }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: turnColor === 'w' ? '#ECE2DB' : '#111115' }} />
                <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 700 }}>{turnColor === 'w' ? 'White to move' : 'Black to move'}</Typography>
              </Box>
              <Box
                onClick={() => setSfEnabled(e => !e)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.5,
                  borderRadius: '10px', cursor: 'pointer',
                  bgcolor: sfEnabled ? `${c.blue}15` : `${c.textMuted}08`,
                  border: `1px solid ${sfEnabled ? `${c.blue}30` : `${c.textMuted}15`}`,
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: sfEnabled ? `${c.blue}20` : `${c.textMuted}12` },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sfEnabled && sfReady ? c.blue : c.textMuted, boxShadow: sfEnabled && sfReady ? `0 0 6px ${c.blue}60` : 'none' }} />
                <Typography sx={{ color: sfEnabled ? c.blue : c.textMuted, fontSize: '0.88rem', fontWeight: 600 }}>Stockfish</Typography>
              </Box>
            </Box>

            {/* Opening context */}
            <Box sx={{ ...cardSx, bgcolor: `${c.accent}06`, border: `1px solid ${c.accent}12` }}>
              <Typography sx={{ color: c.textMuted, fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>Opening</Typography>
              <Typography sx={{ color: c.textDark, fontSize: '1.25rem', fontWeight: 800, mb: 0.8 }}>{selectedMistake.openingName}</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>
                Can you find the right move in this position?
              </Typography>
            </Box>

            {/* Feedback */}
            {puzzleFeedback && (
              <Box sx={{ ...cardSx, bgcolor: puzzleFeedback.correct ? `${c.blue}08` : puzzleFeedback.repeat ? '#E4446E08' : `${c.textMuted}08`, border: `1px solid ${puzzleFeedback.correct ? `${c.blue}25` : puzzleFeedback.repeat ? '#E4446E25' : `${c.textMuted}15`}` }}>
                <Typography sx={{ color: puzzleFeedback.correct ? c.blue : puzzleFeedback.repeat ? '#E4446E' : c.textDark, fontSize: '1.2rem', fontWeight: 800, mb: 0.5 }}>
                  {puzzleFeedback.correct
                    ? 'Well done! That\'s the best move in this position.'
                    : puzzleFeedback.repeat
                    ? 'That\'s the same mistake you made in your game!'
                    : 'Not quite — that\'s not the best move here.'}
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.7 }}>
                  {puzzleFeedback.correct
                    ? <>You found <strong style={{ color: c.blue }}>{puzzleFeedback.bookMove}</strong>. In your game you played <strong style={{ color: '#E4446E' }}>{puzzleFeedback.playerMove}</strong>, which was not the best choice.</>
                    : <>You played <strong style={{ color: c.textDark }}>{puzzleFeedback.movePlayed}</strong>. The best move is <strong style={{ color: c.blue }}>{puzzleFeedback.bookMove}</strong> — in your game you played <strong style={{ color: '#E4446E' }}>{puzzleFeedback.playerMove}</strong>.</>}
                </Typography>
                {/* Stockfish evaluation */}
                {sfEnabled && sfEval && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, p: 1.5, borderRadius: '12px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}12` }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: { great: '#00A5C4', good: '#00A5C4', inaccuracy: '#FFD700', mistake: '#DE7345', blunder: '#E4446E' }[sfEval.classification], flexShrink: 0 }} />
                    <Typography sx={{ color: { great: '#00A5C4', good: '#00A5C4', inaccuracy: '#FFD700', mistake: '#DE7345', blunder: '#E4446E' }[sfEval.classification], fontSize: '1.05rem', fontWeight: 700, textTransform: 'capitalize' }}>
                      {sfEval.classification}
                    </Typography>
                    <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>
                      ({sfEval.drop >= 0 ? '+' : ''}{sfEval.drop} cp)
                    </Typography>
                  </Box>
                )}
                {sfEnabled && puzzleFeedback && !sfEval && (
                  <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', mt: 1, fontStyle: 'italic' }}>Evaluating with Stockfish...</Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                  <Box onClick={() => { handleUndo(); }} sx={{ px: 3, py: 1.2, borderRadius: '12px', cursor: 'pointer', bgcolor: `${c.accent}12`, border: `1.5px solid ${c.accent}30`, display: 'inline-flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: `${c.accent}20` } }}>
                    <Typography sx={{ color: c.accent, fontSize: '1.05rem', fontWeight: 700 }}>Try again</Typography>
                  </Box>
                  {(() => {
                    const siblings = mistakes.filter(m => m.openingName === selectedMistake.openingName && m.playerColor === selectedMistake.playerColor);
                    const idx = siblings.indexOf(selectedMistake);
                    return idx < siblings.length - 1 ? (
                      <Box onClick={() => setSelectedMistake(siblings[idx + 1])} sx={{ px: 3, py: 1.2, borderRadius: '12px', cursor: 'pointer', bgcolor: `${c.textMuted}08`, border: `1px solid ${c.textMuted}15`, display: 'inline-flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: `${c.textMuted}12` } }}>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 700 }}>Next position</Typography>
                      </Box>
                    ) : null;
                  })()}
                </Box>
              </Box>
            )}

            {/* Move history */}
            <Box sx={{ ...cardSx, maxHeight: { xs: 180, md: 260 }, overflow: 'auto' }}>
              <Typography sx={labelSx}>Moves</Typography>
              {movePairs.length === 0 ? (
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontStyle: 'italic' }}>Play a move to begin...</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {movePairs.map((pair) => {
                    const wIsOpening = pair.whiteIdx <= puzzleStartIdx;
                    const bIsOpening = pair.blackIdx <= puzzleStartIdx;
                    return (
                      <React.Fragment key={pair.num}>
                        {pair.whiteIdx === puzzleStartIdx + 1 && <Box sx={{ width: '100%', borderTop: `1px dashed ${c.textMuted}30`, my: 0.8 }} />}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Typography sx={{ color: `${c.textMuted}80`, fontSize: '0.95rem', fontWeight: 600, opacity: wIsOpening ? 0.5 : 1 }}>{pair.num}.</Typography>
                          <Box onClick={() => goTo(pair.whiteIdx)} sx={{ px: 1, py: 0.3, borderRadius: '6px', cursor: 'pointer', bgcolor: moveIndex === pair.whiteIdx ? `${c.accent}25` : 'transparent', '&:hover': { bgcolor: `${c.accent}12` } }}>
                            <Typography sx={{ color: moveIndex === pair.whiteIdx ? c.accent : wIsOpening ? c.textMuted : c.textDark, fontSize: '1rem', fontWeight: moveIndex === pair.whiteIdx ? 700 : 500, opacity: wIsOpening ? 0.5 : 1 }}>{pair.white.san}</Typography>
                          </Box>
                          {pair.black && (
                            <>
                              {pair.blackIdx === puzzleStartIdx + 1 && <Box sx={{ width: '100%', borderTop: `1px dashed ${c.textMuted}30`, my: 0.8 }} />}
                              <Box onClick={() => goTo(pair.blackIdx)} sx={{ px: 1, py: 0.3, borderRadius: '6px', cursor: 'pointer', bgcolor: moveIndex === pair.blackIdx ? `${c.accent}25` : 'transparent', '&:hover': { bgcolor: `${c.accent}12` } }}>
                                <Typography sx={{ color: moveIndex === pair.blackIdx ? c.accent : bIsOpening ? c.textMuted : c.textDark, fontSize: '1rem', fontWeight: moveIndex === pair.blackIdx ? 700 : 500, opacity: bIsOpening ? 0.5 : 1 }}>{pair.black.san}</Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Drill stats */}
            <Box sx={cardSx}>
              <Typography sx={labelSx}>Drill Stats</Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box><Typography sx={{ color: c.textDark, fontSize: '1.8rem', fontWeight: 800 }}>{pStats.total}</Typography><Typography sx={{ color: c.textMuted, fontSize: '0.9rem' }}>Attempted</Typography></Box>
                <Box><Typography sx={{ color: c.blue, fontSize: '1.8rem', fontWeight: 800 }}>{pStats.correct}</Typography><Typography sx={{ color: c.textMuted, fontSize: '0.9rem' }}>Correct</Typography></Box>
                <Box><Typography sx={{ color: c.accent, fontSize: '1.8rem', fontWeight: 800 }}>{accuracy}%</Typography><Typography sx={{ color: c.textMuted, fontSize: '0.9rem' }}>Accuracy</Typography></Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
